import { IconArrowBackUp } from '@tabler/icons-react'
import { AnyFormState } from '@tanstack/form-core'
import { useSelector } from '@tanstack/react-form'
import { AnimatePresence, motion, MotionProps } from 'motion/react'
import { ComponentProps, useState } from 'react'
import { useBlocker } from 'react-router'

import { AstraBadge, AstraButton, AstraCallout, AstraCard, AstraIcon, AstraParagraph } from '~/astra'
import { pluralize } from '~/utils/utils'

import { useFormContext } from './formHookContexts'

const countEditedFields = (state: AnyFormState) => Object.values(state.fieldMeta).reduce((acc, curr) => (curr?.isDirty ? acc + 1 : acc), 0)

const animationProps: MotionProps = {
    initial: {
        opacity: 0,
        y: 10,
        scale: 0.9,
    },
    animate: {
        opacity: 1,
        y: 0,
        scale: 1,
    },
    exit: {
        opacity: 0,
        y: 10,
        scale: 0.9,
    },
    transition: {
        duration: 0.12,
    },
}

const FloatingError = (badgeProps: ComponentProps<typeof AstraBadge>) => (
    // nudge animation
    <motion.div animate={{ x: [0, -5, 5, -3, 3, 0] }} transition={{ duration: 0.35, ease: 'easeInOut' }}>
        <AstraBadge color="critical" {...badgeProps} />
    </motion.div>
)

export const FormFloatingControl = () => {
    const form = useFormContext()
    const { isDirty, isSubmitting, editsCount, showFormSubmitErrors } = useSelector(form.store, state => ({
        isDirty: state.isDirty && !state.isDefaultValue,
        isSubmitting: state.isSubmitting,
        editsCount: countEditedFields(state),
        showFormSubmitErrors: state.submissionAttempts > 0 && state.errors.length > 0,
    }))

    const blocker = useBlocker(isDirty && !isSubmitting)
    const [isConfirmingReset, setIsConfirmingReset] = useState(false)

    const handleReset = () => {
        if (blocker.state === 'blocked') {
            blocker.proceed()
        } else {
            setIsConfirmingReset(true)
        }
    }
    const handleConfirmedReset = () => {
        void form.reset()
        setIsConfirmingReset(false)
    }
    const handleSubmit = () => {
        if (blocker.state === 'blocked') {
            void blocker.reset()
        }
        void form.handleSubmit()
    }
    return (
        <AnimatePresence>
            {isDirty && (
                <motion.div className="sticky bottom-6 z-elevated mx-auto flex w-fit flex-col gap-2" {...animationProps}>
                    {blocker.state === 'blocked' && <FloatingError key={blocker.location.key} text="Du har endringer som ikke er lagret" color="critical" />}

                    <motion.div layout="position" className="flex rounded-xl shadow-md" transition={{ layout: { duration: 0.25 } }}>
                        <AstraCard>
                            <div className="relative flex flex-col gap-2 p-2 px-4">
                                {showFormSubmitErrors && (
                                    <AstraCallout
                                        key="form-submit-errors"
                                        text="Noen felter er ugyldige. Se over de markerte feltene og prøv igjen."
                                        color="critical"
                                    />
                                )}
                                <div className="flex items-center justify-between gap-4">
                                    {editsCount > 0 && (
                                        <>
                                            <div className="flex items-center gap-1 border-r pr-4">
                                                <AstraBadge text={`${editsCount}`} />
                                                <AstraParagraph className="whitespace-nowrap">
                                                    {editsCount === 1 ? 'ulagret' : 'ulagrede'} {pluralize(editsCount, 'endring')}
                                                </AstraParagraph>
                                            </div>
                                        </>
                                    )}
                                    <AnimatePresence mode="popLayout" initial={false}>
                                        {isConfirmingReset ? (
                                            <motion.div key="confirm-reset" layout="position" className="flex flex-wrap items-center gap-1">
                                                <AstraParagraph>Er du sikker på at du vil forkaste endringene?</AstraParagraph>
                                                <motion.div
                                                    initial={{ opacity: 0, x: '-50%' }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.25 }}
                                                    className="ml-auto flex items-center justify-end gap-1"
                                                >
                                                    <AstraButton label="Avbryt" variant="ghost" onClick={() => setIsConfirmingReset(false)} />
                                                    <AstraButton
                                                        variant="critical-secondary"
                                                        label={'Ja, forkast endringer'}
                                                        onClick={handleConfirmedReset}
                                                        isLoading={isSubmitting}
                                                        disabled={isSubmitting}
                                                    />
                                                </motion.div>
                                            </motion.div>
                                        ) : (
                                            <motion.div
                                                key="submit"
                                                layout="position"
                                                className="flex gap-1"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.25 }}
                                            >
                                                <AstraButton
                                                    icon={<AstraIcon icon={IconArrowBackUp} />}
                                                    label="Forkast endringer"
                                                    variant="ghost"
                                                    onClick={handleReset}
                                                />
                                                <AstraButton
                                                    variant="primary"
                                                    label={'Lagre'}
                                                    onClick={handleSubmit}
                                                    isLoading={isSubmitting}
                                                    disabled={isSubmitting}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </AstraCard>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
